import { useEffect, useState, useCallback } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { apiClient } from '@/lib/api-client'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { ChevronLeft, ChevronRight, Search } from 'lucide-react'

interface AdminUser {
  id: string
  name: string
  email: string
  plan: string
  role: string
  status: string
  lastActiveAt: string
  createdAt: string
}

export default function AdminUsersPage() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [users, setUsers] = useState<AdminUser[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)

  const page = parseInt(searchParams.get('page') || '1')
  const pageSize = 25
  const planFilter = searchParams.get('plan') || ''
  const roleFilter = searchParams.get('role') || ''
  const statusFilter = searchParams.get('status') || ''
  const search = searchParams.get('search') || ''

  const loadUsers = useCallback(async () => {
    setLoading(true)
    const resp = await apiClient.listAdminUsers({
      page,
      pageSize,
      plan: planFilter || undefined,
      role: roleFilter || undefined,
      status: statusFilter || undefined,
      search: search || undefined,
    })
    if (resp.success && resp.data) {
      const data = resp.data as { users: AdminUser[]; total: number }
      setUsers(data.users || [])
      setTotal(data.total || 0)
    }
    setLoading(false)
  }, [page, planFilter, roleFilter, statusFilter, search])

  useEffect(() => { loadUsers() }, [loadUsers])

  const updateFilter = (key: string, value: string) => {
    const params = new URLSearchParams(searchParams)
    if (value) {
      params.set(key, value)
    } else {
      params.delete(key)
    }
    params.set('page', '1')
    setSearchParams(params)
  }

  const totalPages = Math.ceil(total / pageSize)

  const roleBadgeVariant = (role: string) => {
    if (role === 'superadmin') return 'default' as const
    if (role === 'admin') return 'secondary' as const
    return 'outline' as const
  }

  const statusBadgeVariant = (status: string) => {
    return status === 'active' ? 'outline' as const : 'destructive' as const
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">User Management</h1>
        <p className="text-sm text-muted-foreground">{total} users total</p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-0 sm:min-w-[200px] max-w-sm">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search by email or name..."
            value={search}
            onChange={(e) => updateFilter('search', e.target.value)}
            className="pl-9 h-9"
          />
        </div>
        <Select value={planFilter} onValueChange={(v) => updateFilter('plan', v === 'all' ? '' : v)}>
          <SelectTrigger className="w-full sm:w-[130px] h-9">
            <SelectValue placeholder="Plan" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All plans</SelectItem>
            <SelectItem value="free">Free</SelectItem>
            <SelectItem value="basic">Basic</SelectItem>
            <SelectItem value="pro">Pro</SelectItem>
            <SelectItem value="enterprise">Enterprise</SelectItem>
          </SelectContent>
        </Select>
        <Select value={roleFilter} onValueChange={(v) => updateFilter('role', v === 'all' ? '' : v)}>
          <SelectTrigger className="w-full sm:w-[130px] h-9">
            <SelectValue placeholder="Role" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All roles</SelectItem>
            <SelectItem value="user">User</SelectItem>
            <SelectItem value="admin">Admin</SelectItem>
            <SelectItem value="superadmin">Superadmin</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={(v) => updateFilter('status', v === 'all' ? '' : v)}>
          <SelectTrigger className="w-full sm:w-[130px] h-9">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="locked">Locked</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
        </div>
      ) : (
        <div className="app-surface rounded-lg overflow-x-auto">
          <table className="w-full text-sm min-w-[600px]">
            <thead>
              <tr className="border-b text-left text-xs font-medium text-muted-foreground">
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Plan</th>
                <th className="px-4 py-3">Role</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Created</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr
                  key={user.id}
                  className="border-b last:border-0 cursor-pointer transition-colors hover:bg-muted/50"
                  onClick={() => navigate(`/admin/users/${user.id}`)}
                  onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/admin/users/${user.id}`) }}
                  tabIndex={0}
                  role="button"
                >
                  <td className="px-4 py-3 font-medium">{user.name || '-'}</td>
                  <td className="px-4 py-3 text-muted-foreground">{user.email}</td>
                  <td className="px-4 py-3">
                    <Badge variant="outline" className="capitalize text-[10px]">{user.plan || 'free'}</Badge>
                  </td>
                  <td className="px-4 py-3">
                    <Badge variant={roleBadgeVariant(user.role)} className="capitalize text-[10px]">{user.role || 'user'}</Badge>
                  </td>
                  <td className="px-4 py-3">
                    <Badge variant={statusBadgeVariant(user.status)} className="text-[10px]">{user.status}</Badge>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground tabular-nums">
                    {new Date(user.createdAt).toLocaleDateString()}
                  </td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">No users found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">
            Page {page} of {totalPages}
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => updateFilter('page', String(page - 1))}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => updateFilter('page', String(page + 1))}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
